import React, { useState, useEffect } from 'react';
import { authorizedFetch } from '../utils/api';
import {
    ChartBarIcon,
    ArrowTrendingUpIcon,
    StarIcon,
    EyeIcon,
    CalendarIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const Analytics = () => {
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timeRange, setTimeRange] = useState('30d');
    const [selectedMetric, setSelectedMetric] = useState('ratings');
    const [renderTime, setRenderTime] = useState(null);
    const [showRenderTime, setShowRenderTime] = useState(false);
    const renderStartRef = React.useRef(null);

    const backendUrl = process.env.REACT_APP_BACKEND2_BASE_URL || 'https://suanfamama-fashion-buyer-production.up.railway.app';

    useEffect(() => {
        fetchAnalyticsData();
    }, [timeRange]);

    const fetchAnalyticsData = async () => {
        renderStartRef.current = performance.now();
        setLoading(true);
        setError(null);

        try {
            // Fetch multiple analytics endpoints
            const [overallStats, brandStats, trendData, recentActivity] = await Promise.all([
                authorizedFetch(`${backendUrl}/analytics/overview?range=${timeRange}`),
                authorizedFetch(`${backendUrl}/analytics/brands?range=${timeRange}`),
                authorizedFetch(`${backendUrl}/analytics/trends?range=${timeRange}`),
                authorizedFetch(`${backendUrl}/analytics/activity?range=${timeRange}`)
            ]);

            const data = {
                overview: overallStats.ok ? await overallStats.json() : null,
                brands: brandStats.ok ? await brandStats.json() : null,
                trends: trendData.ok ? await trendData.json() : null,
                activity: recentActivity.ok ? await recentActivity.json() : null
            };

            setAnalyticsData(data);

            // Performance tracking
            const end = performance.now();
            const seconds = ((end - renderStartRef.current) / 1000).toFixed(2);
            setRenderTime(seconds);
            setShowRenderTime(true);
            setTimeout(() => setShowRenderTime(false), 5000);

        } catch (err) {
            console.error('Error fetching analytics:', err);
            setError('Failed to load analytics data');
        } finally {
            setLoading(false);
        }
    };

    const exportReport = async () => {
        try {
            const response = await authorizedFetch(`${backendUrl}/analytics/export?range=${timeRange}&format=csv`);
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `fashion-analytics-${timeRange}-${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            }
        } catch (err) {
            console.error('Error exporting report:', err);
        }
    };

    // Loading Skeleton
    const LoadingSkeleton = () => (
        <div className="animate-pulse space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                            <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                            <div className="h-4 bg-gray-200 dark:bg-gray-500 rounded"></div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {[...Array(4)].map((_, i) => (
                    <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                        <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
                        <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                ))}
            </div>
        </div>
    );

    // Metric Card Component
    const MetricCard = ({ title, value, change, icon: Icon, color = 'blue' }) => {
        const colorClasses = {
            blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
            green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
            purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
            yellow: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400',
            red: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
        };

        return (
            <div className={`p-6 rounded-lg ${colorClasses[color]}`}>
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium opacity-75">{title}</p>
                        <p className="text-3xl font-bold">{value}</p>
                        {change && (
                            <p className={`text-sm ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {change >= 0 ? '+' : ''}{change}% from last period
                            </p>
                        )}
                    </div>
                    <Icon className="h-8 w-8 opacity-75" />
                </div>
            </div>
        );
    };

    // Brand Performance Component
    const BrandPerformance = ({ brands }) => (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                Brand Performance
            </h3>
            <div className="space-y-4">
                {brands?.slice(0, 10).map((brand, index) => (
                    <div key={brand.name} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-6">
                                #{index + 1}
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                                {brand.name}
                            </span>
                        </div>
                        <div className="flex items-center space-x-4">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                {brand.total_looks} looks
                            </span>
                            <span className={`text-sm font-medium ${brand.avg_score >= 8 ? 'text-green-600' :
                                brand.avg_score >= 6 ? 'text-yellow-600' : 'text-red-600'
                                }`}>
                                {brand.avg_score}/10
                            </span>
                            <div className="w-20 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div
                                    className="bg-blue-500 h-2 rounded-full"
                                    style={{ width: `${(brand.avg_score / 10) * 100}%` }}
                                ></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    // Rating Distribution Component
    const RatingDistribution = ({ distribution }) => (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                Rating Distribution
            </h3>
            <div className="space-y-3">
                {distribution?.map((item) => (
                    <div key={item.score} className="flex items-center">
                        <span className="w-8 text-sm font-medium text-gray-600 dark:text-gray-400">
                            {item.score}★
                        </span>
                        <div className="flex-1 mx-3 bg-gray-200 dark:bg-gray-700 rounded-full h-4">
                            <div
                                className={`h-4 rounded-full ${item.score >= 8 ? 'bg-green-500' :
                                    item.score >= 6 ? 'bg-yellow-500' :
                                        item.score >= 4 ? 'bg-orange-500' : 'bg-red-500'
                                    }`}
                                style={{ width: `${item.percentage}%` }}
                            ></div>
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-400 w-12">
                            {item.count}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );

    // Recent Activity Component
    const RecentActivity = ({ activities }) => (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                Recent Activity
            </h3>
            <div className="space-y-3 max-h-64 overflow-y-auto">
                {activities?.map((activity, index) => (
                    <div key={index} className="flex items-center space-x-3 text-sm">
                        <div className={`w-2 h-2 rounded-full ${activity.type === 'rating' ? 'bg-blue-500' :
                            activity.type === 'collection' ? 'bg-green-500' : 'bg-gray-500'
                            }`}></div>
                        <span className="text-gray-600 dark:text-gray-400">
                            {new Date(activity.timestamp).toLocaleTimeString()}
                        </span>
                        <span className="text-gray-900 dark:text-white">
                            {activity.description}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );

    if (loading) return <LoadingSkeleton />;

    if (error) {
        return (
            <div className="p-4 text-center text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <h2 className="text-lg font-semibold mb-2">Error Loading Analytics</h2>
                <p>{error}</p>
                <button
                    onClick={fetchAnalyticsData}
                    className="mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                >
                    Try Again
                </button>
            </div>
        );
    }

    const overview = analyticsData?.overview || {};

    return (
        <div className="space-y-6 p-4 bg-gray-50 dark:bg-gray-900 min-h-screen">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Fashion Analytics Dashboard
                    </h1>
                    <div className="flex items-center space-x-4">
                        <select
                            value={timeRange}
                            onChange={(e) => setTimeRange(e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            <option value="7d">Last 7 days</option>
                            <option value="30d">Last 30 days</option>
                            <option value="90d">Last 90 days</option>
                            <option value="1y">Last year</option>
                        </select>
                        <button
                            onClick={exportReport}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                        >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            <span>Export</span>
                        </button>
                    </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <MetricCard
                        title="Total Ratings"
                        value={overview.total_ratings || 0}
                        change={overview.ratings_change}
                        icon={StarIcon}
                        color="blue"
                    />
                    <MetricCard
                        title="Avg Rating"
                        value={overview.avg_rating || '0.0'}
                        change={overview.avg_rating_change}
                        icon={ArrowTrendingUpIcon}
                        color="green"
                    />
                    <MetricCard
                        title="Collections Reviewed"
                        value={overview.collections_reviewed || 0}
                        change={overview.collections_change}
                        icon={EyeIcon}
                        color="purple"
                    />
                    <MetricCard
                        title="Active Days"
                        value={overview.active_days || 0}
                        change={overview.activity_change}
                        icon={CalendarIcon}
                        color="yellow"
                    />
                </div>
            </div>

            {/* Charts and Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <BrandPerformance brands={analyticsData?.brands} />
                <RatingDistribution distribution={analyticsData?.trends?.rating_distribution} />
                <RecentActivity activities={analyticsData?.activity} />

                {/* Trends Chart Placeholder */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                        Rating Trends
                    </h3>
                    <div className="h-64 flex items-center justify-center bg-gray-50 dark:bg-gray-700 rounded">
                        <div className="text-center text-gray-500 dark:text-gray-400">
                            <ChartBarIcon className="h-12 w-12 mx-auto mb-2" />
                            <p>Chart visualization coming soon</p>
                            <p className="text-sm">Integration with charting library needed</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Render Time Display */}
            {showRenderTime && (
                <div className="fixed bottom-6 right-6 bg-gray-800 text-white text-xs px-3 py-1 rounded shadow-lg animate-fade-in z-50">
                    Load Time: {renderTime} seconds
                </div>
            )}
        </div>
    );
};

export default Analytics;